import { Asset, AssetState, FolderContent } from '@/api/generatedApi';
import { isAfter, parseISO } from 'date-fns';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useTenants } from '@/services/tenants/useTenants';
import { isLabelPending } from '@/utils/assets';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { GET_ASSET_THUMBNAIL_QUERY_KEY } from '../api/assets/useGetAssetThumbnail';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '../api/folders/useFetchFolderContents';
import { useFolderColumnVisibility } from '../api/folders/useFolderColumnVisibility';
import { RefetchManager } from './refetchManager';

const needsPolling = (row: Pick<Asset, 'state' | 'label'>): boolean =>
  row.state === AssetState.Indexing || isLabelPending(row);

export const useAssetPolling = (parameters: { row: FolderContent }) => {
  const refetchManager = RefetchManager.getInstance();
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const wasPollingReference = useRef(false);
  const thumbnailRefreshedReference = useRef(false);
  const { ensureFresh: ensureColumnVisibilityFresh } = useFolderColumnVisibility({
    folderId: parameters.row.contentType === 'asset' ? parameters.row.folderId : undefined,
  });

  const { asset: fetchedAsset } = useGetAsset({
    assetId: parameters.row.id,
    enabled: false,
  });

  const asset =
    !fetchedAsset || isAfter(parseISO(parameters.row.updatedAt), parseISO(fetchedAsset.updatedAt))
      ? (parameters.row as Asset)
      : fetchedAsset;

  useEffect(() => {
    refetchManager.configure({ tenantId, queryClient });
  }, [refetchManager, tenantId, queryClient]);

  useEffect(() => {
    if (parameters.row.contentType !== 'asset') return;
    if (!needsPolling(parameters.row as Asset)) return;
    wasPollingReference.current = true;
    refetchManager.register(parameters.row.id);
  }, [parameters.row.id, parameters.row.contentType, parameters.row, refetchManager]);

  useEffect(() => {
    if (!fetchedAsset) return;
    if (
      wasPollingReference.current &&
      !thumbnailRefreshedReference.current &&
      fetchedAsset.state === AssetState.Finished
    ) {
      const thumbnailQueryKey = [GET_ASSET_THUMBNAIL_QUERY_KEY, { assetId: parameters.row.id }];
      const thumbnailState = queryClient.getQueryState(thumbnailQueryKey);
      if (thumbnailState?.status === 'error') {
        thumbnailRefreshedReference.current = true;
        queryClient.resetQueries({ queryKey: thumbnailQueryKey });
      }
    }

    if (needsPolling(fetchedAsset)) return;

    refetchManager.unregister(parameters.row.id);

    if (wasPollingReference.current && fetchedAsset.state === AssetState.Finished) {
      wasPollingReference.current = false;
      ensureColumnVisibilityFresh('label');
      if (fetchedAsset.isRootAssembly) {
        queryClient.resetQueries({
          queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
          exact: false,
        });
      }
    }
  }, [fetchedAsset, parameters.row.id, refetchManager, queryClient, ensureColumnVisibilityFresh]);

  useEffect(() => {
    return () => {
      refetchManager.unregister(parameters.row.id);
    };
  }, [parameters.row.id, refetchManager]);

  return { data: asset, fetchedData: fetchedAsset };
};
